/**
 * Button Component
 * Step 4 - Design System Refinement
 *
 * Enhanced with isLoading prop for consistent loading states.
 */

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center whitespace-nowrap font-medium border-solid border-[1px] border-transparent rounded-[28px] ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 transition-all duration-300", {
  variants: {
    variant: {
      default: cn("button-bg-border-default-pill", "text-input-fg bg-button-bg/50 hover:bg-button-bg-hover", "black:bg-[radial-gradient(ellipse_90%_120%_at_50%_50%,hsla(220,0%,10%,0.5)_0%,hsla(220,0%,0%,0.5)_50%)]", "black:border-white/[0.10]"),
      primary: cn(
      // Global
      "border-blue-400/25", "text-white bg-primary-500 hover:bg-primary-400 hover:shadow-[0_0_24px_hsla(220,100%,50%,0.3)]", "text-white bg-[radial-gradient(ellipse_90%_120%_at_50%_80%,hsla(220,100%,70%,.5)_0%,hsla(220,100%,30%,.5)_50%)] shadoww-[inset_-4px_-4px_8px_rgba(0,100,255,.2),inset_4px_4px_5px_rgba(0,0,80,0.3)]",
      // Dark Mode
      "dark:bg-primary-600 dark:hover:bg-primary-500", "dark:bg-[radial-gradient(ellipse_90%_120%_at_50%_50%,hsla(220,100%,50%,.5)_0%,hsla(220,100%,20%,.5)_50%)]"),
      secondary: cn("bg-secondary-600 text-input-fg shadow-[inset_-4px_-4px_5px_rgba(100,200,255,.1),inset_4px_4px_5px_rgba(0,0,80,0.1)]", "hover:bg-secondary-500 hover:shadow-[inset_-4px_-4px_5px_rgba(100,200,255,.4),inset_4px_4px_5px_rgba(40,40,0,0.3)]", "border-[#00ffcc]/25 dark:border-white/[0.10]"),
      warning: cn("bg-amber-800/40 text-input-fg shadow-[inset_-4px_-4px_5px_rgba(255,255,0,.1),inset_4px_4px_5px_rgba(0,80,80,0.01)]", "hover:bg-amber-700", "border-amber-500/25 dark:border-amber-500/25"),
      destructive: cn("bg-red-800/40 text-input-fg shadow-[inset_-4px_-4px_5px_rgba(255,100,100,.1),inset_4px_4px_5px_rgba(80,0,0,0.01)]", "hover:bg-red-800", "border-red-500/25 dark:border-red-500/25"),
      // V1 Button variants
      accent: "bg-accent-gradient tradedevi:bg-accent-gradient-tradedevi text-white hover:bg-accent-gradient/90 ",
      destructiveMimimal: "bg-red-500/0 border border-red-500/0 text-red-500 hover:bg-red-500/20 hover:border-red-500/20",
      outline: "border border-input-fg/60 bg-transparent hover:bg-input-fg/10 hover:text-accent-foreground tradedevi:hover:bg-card/50 border-black/[0.12] dark:border-white/[0.10]",
      outlineBg: cn("border border-button-border bg-button-bg hover:bg-button-bg-hover hover:text-accent-foreground"),
      outlineBgPrimary: cn("relative border border-button-bg-secondary bg-transparent", 'after:content-[""] after:absolute after:inset-1 after:z-[-1] after:bg-primary-500 after:rounded-[0.7rem] hover:after:bg-primary-600 after:transition-all after:duration-300'),
      outlineDim: cn("backdrop-blur-[1rem] border bg-input opacity-60 shadow-[inset_-4px_-4px_5px_rgba(255,255,255,0.02),inset_4px_4px_5px_rgba(0,0,0,0.1)] transition-all duration-300", "hover:bg-input-hover/20", "border-black/[0.12] dark:border-white/[0.10]"),
      outlineDimHoverRed: cn("backdrop-blur-[1rem] border bg-transparent border-input-border/20 shadow-[inset_-4px_-4px_5px_rgba(255,255,255,0.02),inset_4px_4px_5px_rgba(0,0,0,0.1)] transition-all duration-300", "hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/20 hover:shadow-[inset_-4px_-4px_5px_rgba(255,100,100,0.1),inset_4px_4px_5px_rgba(100,0,0,0.1)]"),
      outlinePrimary: "bg-transparent border border-primary-500 shadow-[inset_-2px_-2px_5px_rgba(0,0,255,.5),inset_4px_4px_5px_rgba(0,0,80,0.3)] hover:bg-primary-500/30 hover:text-primary-foreground",
      outlinePrimaryGlow: "bg-primary-500-10 drop-shadow-[0_0_16px_hsla(225,100%,50%,0.8)] border border-primary-500 hover:bg-primary-500-30 hover:text-primary-foreground tradedevi:hover:bg-card/50 tradedevi:hover:border-transparent tradedevi:hover:text-primary-foreground",
      outlineDimHoverPrimary: "border border-input-fg/10 bg-transparent hover:shadow-[inset_12px_12px_32px_hsla(225,100%,50%,0.4)] hover:border-primary-500 hover:drop-shadow-[0_0_16px_hsla(225,100%,50%,0.8)]",
      outlineGreen: "bg-green-500/10 border border-green-500 hover:bg-green-500/20 hover:text-green-500",
      outlineRed: "bg-red-500/10 border border-red-500 hover:bg-red-500/20 hover:text-red-500 border-red-500/25 dark:border-red-500/25",
      ghost: "border-none hover:bg-input-fg/10 hover:text-input-fg",
      ghostDim: "border-none text-muted-foreground hover:bg-input-fg/10 hover:text-input-fg",
      link: "text-primary underline-offset-4 hover:underline",
      app: "bg-app text-app-fg hover:bg-app-fg/10",
      primaryText: cn("bg-transparent text-primary-400", "hover:text-input-fg"),
      dimHoverPrimary: cn("bg-primary-400/0 text-blue-500", "hover:text-input-fg hover:bg-primary-500 hover:shadow-[inset_-4px_-4px_5px_rgba(100,200,255,.4),inset_4px_4px_5px_rgba(0,0,80,0.3)]"),
      dimHoverRed: cn("bg-red-500/0 text-red-500", "hover:text-red-100 hover:bg-red-500/20 hover:shadow-[inset_-4px_-4px_5px_rgba(255,0,0,.2),inset_4px_4px_5px_rgba(80,0,0,0.5)]")
    },
    size: {
      default: "h-10 px-4 py-2 text-sm",
      md: "h-9 px-4 py-1",
      sm: "h-8 px-3 text-xs",
      xs: "h-6 px-3 text-xs font-normal",
      lg: "h-12 px-8",
      icon: "h-10 w-10"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Shows a loading spinner and disables the button
   * @default false
   */
  isLoading?: boolean;
  /**
   * Text to show while loading (replaces children)
   * If not provided, children are preserved alongside spinner
   */
  loadingText?: string;
  /**
   * Position of the loading spinner
   * @default "left"
   */
  spinnerPosition?: "left" | "right";
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  loadingText,
  spinnerPosition = "left",
  disabled,
  children,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";

  // When loading, disable the button
  const isDisabled = disabled || isLoading;

  // Loading spinner element
  const spinner = isLoading ? <Loader2Icon className={cn("h-4 w-4 animate-spin",
  // Add margin based on position if there are children
  children || loadingText ? spinnerPosition === "left" ? "mr-2" : "ml-2" : "")} aria-hidden="true" /> : null;

  // Determine content based on loading state
  const content = isLoading ? <>
        {spinnerPosition === "left" && spinner}
        {loadingText || children}
        {spinnerPosition === "right" && spinner}
      </> : children;
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    className
  }))} ref={ref} disabled={isDisabled} aria-busy={isLoading} aria-disabled={isDisabled} {...props}>
        {content}
      </Comp>;
});
Button.displayName = "Button";

/* =================================================================
   ICON BUTTON
   A button optimized for icon-only usage with proper accessibility
   ================================================================= */

interface IconButtonProps extends ButtonProps {
  /**
   * Accessible label for screen readers (required for icon-only buttons)
   */
  "aria-label": string;
}
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(({
  size = "icon",
  className,
  ...props
}, ref) => {
  return <Button ref={ref} size={size} className={cn("flex-shrink-0", className)} {...props} />;
});
IconButton.displayName = "IconButton";
export { Button, buttonVariants, IconButton };